// TODO: clean this component and break down in a folder

// NOTE: hooks
import { useState, useMemo } from 'react'
import { NavLink, Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useAuthContext } from '../../hooks/useAuthContext'
import useFirebaseFile from '../../hooks/useFirebaseFile'
import { usePermission } from '../../hooks/usePermission'
import { toast } from 'sonner'
// NOTE: components
import Module from './Card'
import Modal from './Modal'
import Tooltip from '@mui/material/Tooltip'
// NOTE: images
import helfyRectangular from '../../assets/img/logo/helfyLogo_rect.png'
import avatarPlaceholderImage from '../../assets/img/placeholder.png'
import clinicianIcon from '../../assets/img/logo/clinicIcon.png'
import helfyIcon from '../../assets/img/logo/helfyIcon.png'
import companyPlaceholderImage from '../../assets/img/placeholder-logo.png'
import {
  FaGear as SettingsIcon,
  FaArrowRightFromBracket as LogoutIcon
} from 'react-icons/fa6'
import { FaQuestionCircle as HelpIcon } from 'react-icons/fa'

interface NavBarProps {
  links: any
  home: any
}

const NavBar = ({ links, home }: NavBarProps) => {
  const { user } = useAuthContext()
  const { logout } = useAuth()
  const { canView, canWrite } = usePermission()
  const [show, setShow] = useState(false)

  const {
    fileUrl: profilePictureUrl,
    isLoadingFileUrl: isLoadingProfilePicture,
    isErrorFileUrl: isErrorProfilePicture,
    errorFileUrl: errorProfilePicture
  } = useFirebaseFile(user?.profilePicture)

  const {
    fileUrl: companyLogoUrl,
    isLoadingFileUrl: isLoadingCompanyLogo,
    isErrorFileUrl: isErrorCompanyLogo,
    errorFileUrl: errorCompanyLogo
  } = useFirebaseFile(user?.team?.companyLogo)

  // NOTE: Filter links based on permissions
  const visibleLinks = useMemo(() => {
    return links.filter((link: any) => {
      // NOTE: Remove leading/trailing slashes
      const parentPath = link.to.replace(/^\//, '').replace(/\/$/, '')
      const isManager = user?.team?.managers?.includes(user?.email)

      // NOTE: Map routes to permission pages
      if (parentPath === 'home') return canView('home')
      if (parentPath === 'patients') return canView('patients')
      if (parentPath === 'services') return canView('services') && isManager
      if (parentPath === 'settings') return canView('settings')

      return true
    })
  }, [links, canView, user?.team, user?.email])

  if (isErrorCompanyLogo) toast.error("Couldn't load company logo", { description: errorCompanyLogo?.message })
  if (isErrorProfilePicture) toast.error("Couldn't load profile picture", { description: errorProfilePicture?.message })

  return (
    <>
      <Modal show={show} onHide={() => setShow(false)} size="sm">
        <Modal.Header onHide={() => setShow(false)}>Hi, {user?.fname}!</Modal.Header>

        <div className="border-b-2 border-[#f6f8f9] p-4">
          <NavLink
            className="flex-fill flex w-full flex-col"
            to="/settings/personal"
            onClick={() => setShow(false)}
          >
            {isLoadingProfilePicture && (
              <div
                style={{ borderRadius: '50%', padding: 2, border: 'solid #66CCCC' }}
                className="skeleton mx-auto size-28 rounded-full p-1"
              />
            )}

            {!isLoadingProfilePicture && (
              <img
                style={{
                  borderRadius: '50%',
                  padding: 2,
                  border: 'solid #66CCCC'
                }}
                src={profilePictureUrl || avatarPlaceholderImage}
                alt="Profile"
                className="mx-auto h-28 rounded-full p-1 shadow-inner"
                loading="eager"
              />
            )}
            <p className="font-regular mt-2 text-center text-lg">{user?.email}</p>
          </NavLink>
        </div>

        <div className="divide-y divide-slate-100 px-4 pb-4 text-slate-600">
          <NavLink
            to="/settings/help"
            onClick={() => setShow(false)}
            className="my-2 flex gap-3 px-0 py-4 text-xl sm:gap-4 sm:px-4 text-slate-700"
          >
            <HelpIcon className="my-auto" />
            <p className="my-auto font-medium">Help</p>
          </NavLink>

          <NavLink
            className="my-2 flex gap-3 px-0 py-4 text-xl sm:gap-4 sm:px-4 text-slate-700"
            to={canWrite('settings/clinic') ? '/settings/clinic' : '/settings/personal'}
            onClick={() => setShow(false)}
          >
            <SettingsIcon className="my-auto" />
            <p className="my-auto font-medium">Settings</p>
          </NavLink>

          <button
            className="my-2 flex w-full gap-3 px-0 py-4 text-xl sm:gap-4 sm:px-4 text-slate-700"
            onClick={logout}
          >
            <LogoutIcon className="my-auto" />
            <p className="my-auto font-medium">Log out</p>
          </button>
        </div>
      </Modal>

      <nav>
        <PhoneNav
          links={visibleLinks}
          profilePicture={profilePictureUrl || avatarPlaceholderImage}
          isLoadingProfilePicture={isLoadingProfilePicture}
          openAccount={() => setShow(true)}
          className='block md:hidden'
        />

        <DeskNav
          links={visibleLinks}
          home={home}
          profilePicture={profilePictureUrl || avatarPlaceholderImage}
          isLoadingProfilePicture={isLoadingProfilePicture}
          companyLogo={user?.team ? (companyLogoUrl || companyPlaceholderImage) : clinicianIcon}
          isLoadingCompanyLogo={!!user?.team && isLoadingCompanyLogo}
          openAccount={() => setShow(true)}
          className='hidden md:block'
        />
      </nav>
    </>
  )
}

interface PhoneNavProps {
  links: any
  profilePicture: string
  isLoadingProfilePicture: boolean
  openAccount: any
  className: string
}

const PhoneNav = ({ links, profilePicture, isLoadingProfilePicture, openAccount, className }: PhoneNavProps) => {
  return (
    <Module
      className={`${className} no-scrollbar fixed bottom-0 z-20`}
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        boxShadow: 'rgba(149, 157, 165, 0.2) 0px 0px 24px',
        overflowX: 'scroll',
        border: 'none',
        borderRadius: '0',
        width: '100%'
      }}
    >
      <div className='flex justify-around p-2'>
        {links.map((link: any) => (
          <div key={link.to}>
            <NavLink
              className={({ isActive }) =>
                `${isActive ? 'active' : null} nav-link flex h-full flex-col p-3`
              }
              to={link.to}
              end={link.text?.toLowerCase() === 'home'}
            >
              {({ isActive }) => (
                <Tooltip title={link.text}>
                  <span className="mx-auto h-auto w-[34px] transition-all duration-700 ease-in-out">
                    {isActive ? link.activeIcon : link.inactiveIcon}
                  </span>
                </Tooltip>
              )}
            </NavLink>
          </div>
        ))}
        <button onClick={openAccount}>
          {!isLoadingProfilePicture && (
            <Tooltip title='Account'>
              <img
                src={profilePicture}
                style={{
                  width: 40,
                  height: 40,
                  maxWidth: 40,
                  maxHeight: 40,
                  borderRadius: '50%',
                  padding: 2,
                  border: '1.5px solid #66CCCC'
                }}
                className='shadow-inner'
                alt='Profile'
                loading='eager'
              />
            </Tooltip>
          )}

          {isLoadingProfilePicture && (
            <div
              className='skeleton'
              style={{
                width: 40,
                height: 40,
                maxWidth: 40,
                maxHeight: 40,
                borderRadius: '50%',
                padding: 2,
                border: '1.5px solid #66CCCC'
              }}
            />
          )}
        </button>
      </div>
    </Module>
  )
}

interface DeskNavProps {
  links: any
  home: any
  companyLogo: string
  isLoadingCompanyLogo: boolean
  profilePicture: string
  isLoadingProfilePicture: boolean
  openAccount: any
  className: string
}

const DeskNav = ({ links, home, companyLogo, isLoadingCompanyLogo, profilePicture, isLoadingProfilePicture, openAccount, className }: DeskNavProps) => {
  const { user } = useAuthContext()

  return (
    <div
      style={{
        height: '100vh',
        flexShrink: 0,
        backgroundColor: '#f1f1f1',
        position: 'sticky',
        bottom: 0,
        top: 0
      }}
      className={`${className} w-[110px] lg:w-[260px]`}
    >
      <div
        style={{
          backgroundColor: 'white',
          border: '2px solid rgba(51, 66, 81, 0.1)',
          width: '100%',
          height: '100%'
        }}
      >
        <div className='flex flex-col p-2' style={{ height: '100%', overflowY: 'scroll' }}>
          <Link to={home} className='mx-auto mb-8 mt-6 w-full px-2 lg:px-4'>
            {!isLoadingCompanyLogo && (
              <img
                src={companyLogo}
                alt={user?.team?.name ?? 'Clinic'}
                className="mx-auto max-h-[65px] lg:max-h-[120px]"
                loading="eager"
              />
            )}
            {isLoadingCompanyLogo && <div className="skeleton h-[78px] w-full rounded" />}
          </Link>

          {links.map((link: any) => (
            <div key={link.to}>
              <NavLink
                className={({ isActive }) =>
                  `${isActive ? 'active' : null} nav-link mt-4 flex px-4 py-2`
                }
                to={link.to}
                end={link.text?.toLowerCase() === 'home'}
              >
                {({ isActive }) => (
                  <>
                    <span className="mx-auto h-auto w-[34px] transition-all duration-700 ease-in-out lg:mx-0 lg:w-[30px]">
                      {isActive ? link.activeIcon : link.inactiveIcon}
                    </span>
                    <span className='my-auto hidden pl-4 lg:inline'>{link.text}</span>
                  </>
                )}
              </NavLink>
            </div>
          ))}

          <div className='mt-auto flex flex-col'>
            <button className='nav-link mb-8 mt-4 flex px-0 py-2 lg:px-4' onClick={openAccount}>
              {!isLoadingProfilePicture && (
                <img
                  src={profilePicture}
                  style={{
                    borderRadius: '50%',
                    padding: 2,
                    border: '1.5px solid #66CCCC'
                  }}
                  className='mx-auto size-[55px] shrink-0 shadow-inner lg:mx-0 lg:size-[40px]'
                  alt='Profile'
                  loading='eager'
                />
              )}
              {isLoadingProfilePicture && (
                <div
                  style={{
                    borderRadius: '50%',
                    padding: 2,
                    border: '1.5px solid #66CCCC'
                  }}
                  className='skeleton mx-auto size-[55px] shrink-0 lg:mx-0 lg:size-[40px]'
                />
              )}
              <span className='my-auto hidden pl-4 lg:inline'>Account</span>
            </button>

            <hr />

            <div className="my-2 px-3 py-2 text-center">
              <img
                src={helfyIcon}
                alt="helfy"
                className="mx-auto h-[40px] lg:hidden"
                loading="eager"
              />

              <img
                src={helfyRectangular}
                alt="helfy rectangular"
                className="mx-auto h-[30px] hidden lg:block"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NavBar
